import clsx from 'clsx';
import {
	Button,
	FormFileUpload,
	Placeholder,
	DropZone,
	__experimentalInputControl as WCInputControl,
	__experimentalInputControlSuffixWrapper as InputControlSuffixWrapper,
	withFilters,
} from '@wordpress/components';
import { __, _x } from '@wordpress/i18n';
import { useState, useEffect, useMemo } from '@wordpress/element';
import { useSelect } from '@wordpress/data';
import { keyboardReturn } from '@wordpress/icons';
import deprecated from '@wordpress/deprecated';
import type { ChangeEvent, FormEvent, ReactNode } from 'react';
import MediaUpload from '../media-upload';
import MediaUploadCheck from '../media-upload/check';
import URLPopover from '../url-popover';
import { store as blockEditorStore } from '../../store';
import { parseDropEvent } from '../use-on-block-drop';
import { getComputedAcceptAttribute } from './utils';
import type { MediaUploadFunction } from '../provider/types';
import type {
	InsertFromURLPopoverProps,
	MediaPlaceholderMedia,
	MediaPlaceholderProps,
	URLSelectionUIProps,
} from './types';
const noop = () => {};

const InsertFromURLPopover = ( {
	src,
	onChange,
	onSubmit,
	onClose,
	popoverAnchor,
}: InsertFromURLPopoverProps ) => (
	<URLPopover anchor={ popoverAnchor } onClose={ onClose }>
		<form
			className="block-editor-media-placeholder__url-input-form"
			onSubmit={ onSubmit }
		>
			<WCInputControl
				label={ __( 'URL' ) }
				type="text" // Use text instead of URL to allow relative paths (e.g., /image/image.jpg)
				hideLabelFromVision
				placeholder={ __( 'Paste or type URL' ) }
				onChange={ onChange }
				value={ src }
				suffix={
					<InputControlSuffixWrapper variant="control">
						<Button
							size="small"
							icon={ keyboardReturn }
							label={ __( 'Apply' ) }
							type="submit"
						/>
					</InputControlSuffixWrapper>
				}
			/>
		</form>
	</URLPopover>
);

const URLSelectionUI = ( {
	src,
	onChangeSrc,
	onSelectURL,
}: URLSelectionUIProps ) => {
	// Use internal state instead of a ref to make sure that the component
	// re-renders when the popover's anchor updates.
	const [ popoverAnchor, setPopoverAnchor ] = useState< HTMLElement | null >(
		null
	);
	const [ isURLInputVisible, setIsURLInputVisible ] = useState( false );

	const openURLInput = () => {
		setIsURLInputVisible( true );
	};
	const closeURLInput = () => {
		setIsURLInputVisible( false );
		popoverAnchor?.focus();
	};

	const onSubmitSrc = ( event: FormEvent< HTMLFormElement > ) => {
		event.preventDefault();
		if ( src && onSelectURL ) {
			onSelectURL( src );
			closeURLInput();
		}
	};

	return (
		<div className="block-editor-media-placeholder__url-input-container">
			<Button
				__next40pxDefaultSize
				className="block-editor-media-placeholder__button"
				onClick={ openURLInput }
				isPressed={ isURLInputVisible }
				variant="secondary"
				aria-haspopup="dialog"
				ref={ setPopoverAnchor }
			>
				{ __( 'Insert from URL' ) }
			</Button>
			{ isURLInputVisible && (
				<InsertFromURLPopover
					src={ src }
					onChange={ onChangeSrc }
					onSubmit={ onSubmitSrc }
					onClose={ closeURLInput }
					popoverAnchor={ popoverAnchor }
				/>
			) }
		</div>
	);
};

export function MediaPlaceholder( {
	value = {},
	allowedTypes,
	className,
	icon,
	labels = {},
	mediaPreview,
	notices,
	isAppender,
	accept,
	addToGallery,
	multiple = false,
	handleUpload = true,
	disableDropZone,
	disableMediaButtons,
	onError,
	onSelect,
	onCancel,
	onSelectURL,
	onToggleFeaturedImage,
	onDoubleClick,
	onFilesPreUpload = noop,
	onHTMLDrop: deprecatedOnHTMLDrop,
	children,
	mediaLibraryButton,
	placeholder,
	style,
}: MediaPlaceholderProps ) {
	if ( deprecatedOnHTMLDrop ) {
		deprecated( 'wp.blockEditor.MediaPlaceholder onHTMLDrop prop', {
			since: '6.2',
			version: '6.4',
		} );
	}

	const { mediaUpload, allowedMimeTypes } = useSelect( ( select ) => {
		const { getSettings } = select( blockEditorStore );
		const settings = getSettings();
		return {
			mediaUpload: settings.mediaUpload as
				MediaUploadFunction | undefined,
			allowedMimeTypes: settings.allowedMimeTypes,
		};
	}, [] );
	const [ src, setSrc ] = useState< string | undefined >( '' );

	useEffect( () => {
		// Galleries have no `src`.
		setSrc( ( value as MediaPlaceholderMedia )?.src ?? '' );
	}, [ ( value as MediaPlaceholderMedia )?.src ] );

	const computedAccept = useMemo(
		() =>
			getComputedAcceptAttribute(
				allowedTypes,
				allowedMimeTypes,
				accept
			),
		[ allowedTypes, allowedMimeTypes, accept ]
	);

	const onlyAllowsImages = () => {
		if ( ! allowedTypes || allowedTypes.length === 0 ) {
			return false;
		}

		return allowedTypes.every(
			( allowedType ) =>
				allowedType === 'image' || allowedType.startsWith( 'image/' )
		);
	};

	const onFilesUpload = ( files: ArrayLike< File > ) => {
		if (
			! handleUpload ||
			( typeof handleUpload === 'function' && ! handleUpload( files ) )
		) {
			return onSelect( files );
		}
		onFilesPreUpload( files );
		let setMedia: ( media: MediaPlaceholderMedia[] ) => void;
		if ( multiple ) {
			if ( addToGallery ) {
				// Since the setMedia function runs multiple times per upload group
				// and is passed newMedia containing every item in its group each time, we must
				// filter out whatever this upload group had previously returned to the
				// gallery before adding and returning the image array with replacement newMedia
				// values.

				// Define an array to store urls from newMedia between subsequent function calls.
				let lastMediaPassed: { id?: number; urlSlug: string }[] = [];
				setMedia = ( newMedia ) => {
					// Remove any images this upload group is responsible for (lastMediaPassed).
					// Their replacements are contained in newMedia.
					// Galleries pass their list of media.
					const filteredMedia = (
						( value as MediaPlaceholderMedia[] ) ?? []
					).filter( ( item ) => {
						// If Item has id, only remove it if lastMediaPassed has an item with that id.
						if ( item.id ) {
							return ! lastMediaPassed.some(
								// Be sure to convert to number for comparison.
								( { id } ) => Number( id ) === Number( item.id )
							);
						}
						// Compare transient images via .includes since gallery may append extra info onto the url.
						// Uploaded media have a URL.
						return ! lastMediaPassed.some( ( { urlSlug } ) =>
							item.url!.includes( urlSlug )
						);
					} );
					// Return the filtered media array along with newMedia.
					onSelect( filteredMedia.concat( newMedia ) );
					// Reset lastMediaPassed and set it with ids and urls from newMedia.
					lastMediaPassed = newMedia.map( ( media ) => {
						// Add everything up to '.fileType' to compare via .includes.
						const cutOffIndex = media.url!.lastIndexOf( '.' );
						const urlSlug = media.url!.slice( 0, cutOffIndex );
						return { id: media.id, urlSlug };
					} );
				};
			} else {
				setMedia = onSelect;
			}
		} else {
			setMedia = ( [ media ] ) => onSelect( media );
		}
		// Uploads only start where the user can upload media.
		mediaUpload!( {
			allowedTypes,
			filesList: files,
			// The upload passes the uploaded media.
			onFileChange: setMedia as (
				attachments: Array< Record< string, unknown > >
			) => void,
			onError,
			// `add` allows multiple files like `true`.
			multiple: multiple as boolean,
		} );
	};

	async function handleBlocksDrop( event: globalThis.DragEvent ) {
		const { blocks } = parseDropEvent( event );

		if ( ! blocks?.length ) {
			return;
		}

		const uploadedMediaList = await Promise.all(
			blocks.map( ( block ) => {
				const blockType = block.name.split( '/' )[ 1 ];
				if ( block.attributes.id ) {
					block.attributes.type = blockType;
					return block.attributes;
				}
				return new Promise( ( resolve, reject ) => {
					window
						// Media blocks without an ID have a URL.
						.fetch( block.attributes.url as string )
						.then( ( response ) => response.blob() )
						.then( ( blob ) =>
							// Uploads only start where the user can upload media.
							mediaUpload!( {
								// The upload reads a blob like a file.
								filesList: [ blob as File ],
								additionalData: {
									title: block.attributes.title,
									alt_text: block.attributes.alt,
									caption: block.attributes.caption,
									type: blockType,
								},
								onFileChange: ( [ media ] ) => {
									if ( media.id ) {
										resolve( media );
									}
								},
								allowedTypes,
								onError: reject,
							} )
						)
						.catch( () => resolve( block.attributes.url ) );
				} );
			} )
			// Callers that accept dropped blocks handle errors.
		).catch( ( err ) => onError!( err ) );

		if ( ! uploadedMediaList?.length ) {
			return;
		}

		onSelect( multiple ? uploadedMediaList : uploadedMediaList[ 0 ] );
	}

	const onUpload = ( event: ChangeEvent< HTMLInputElement > ) => {
		// A file input has a file list.
		onFilesUpload( event.target.files! );
	};

	const defaultRenderPlaceholder = ( content: ReactNode ) => {
		let { instructions, title } = labels;

		if ( ! mediaUpload && ! onSelectURL ) {
			instructions = __(
				'To edit this block, you need permission to upload media.'
			);
		}

		if ( instructions === undefined || title === undefined ) {
			const typesAllowed = allowedTypes ?? [];

			const [ firstAllowedType ] = typesAllowed;
			const isOneType = 1 === typesAllowed.length;
			const isAudio = isOneType && 'audio' === firstAllowedType;
			const isImage = isOneType && 'image' === firstAllowedType;
			const isVideo = isOneType && 'video' === firstAllowedType;

			if ( instructions === undefined && mediaUpload ) {
				instructions = __(
					'Drag and drop an image or video, upload, or choose from your library.'
				);

				if ( isAudio ) {
					instructions = __(
						'Drag and drop an audio file, upload, or choose from your library.'
					);
				} else if ( isImage ) {
					instructions = __(
						'Drag and drop an image, upload, or choose from your library.'
					);
				} else if ( isVideo ) {
					instructions = __(
						'Drag and drop a video, upload, or choose from your library.'
					);
				}
			}

			if ( title === undefined ) {
				title = __( 'Media' );

				if ( isAudio ) {
					title = __( 'Audio' );
				} else if ( isImage ) {
					title = __( 'Image' );
				} else if ( isVideo ) {
					title = __( 'Video' );
				}
			}
		}

		const placeholderClassName = clsx(
			'block-editor-media-placeholder',
			className,
			{
				'is-appender': isAppender,
			}
		);

		return (
			<Placeholder
				icon={ icon }
				label={ title }
				instructions={ instructions }
				className={ placeholderClassName }
				notices={ notices }
				onDoubleClick={ onDoubleClick }
				preview={ mediaPreview }
				style={ style }
			>
				{ content }
				{ children }
			</Placeholder>
		);
	};
	const renderPlaceholder = placeholder ?? defaultRenderPlaceholder;

	const renderDropZone = () => {
		if ( disableDropZone ) {
			return null;
		}

		return (
			<DropZone
				onFilesDrop={ onFilesUpload }
				onDrop={ handleBlocksDrop }
				isEligible={ ( dataTransfer ) => {
					// This dropzone only accepts block drags from the inserter, not
					// canvas block drags. Only the inserter publishes the dragged
					// blocks' names, as wp-block:core/{name}, so no matches means it
					// isn't an inserter drag.
					const prefix = 'wp-block:core/';
					const types = [];
					for ( const type of dataTransfer.types ) {
						if ( type.startsWith( prefix ) ) {
							types.push( type.slice( prefix.length ) );
						}
					}
					return (
						types.length > 0 &&
						types.every( ( type ) =>
							// Callers that accept dropped blocks set the allowed types.
							allowedTypes!.includes( type )
						) &&
						( multiple ? true : types.length === 1 )
					);
				} }
			/>
		);
	};

	const renderCancelLink = () => {
		return (
			onCancel && (
				<Button
					__next40pxDefaultSize
					className="block-editor-media-placeholder__cancel-button"
					title={ __( 'Cancel' ) }
					variant="link"
					onClick={ onCancel }
				>
					{ __( 'Cancel' ) }
				</Button>
			)
		);
	};

	const renderUrlSelectionUI = () => {
		return (
			onSelectURL && (
				<URLSelectionUI
					src={ src }
					onChangeSrc={ setSrc }
					onSelectURL={ onSelectURL }
				/>
			)
		);
	};

	const renderFeaturedImageToggle = () => {
		return (
			onToggleFeaturedImage && (
				<div className="block-editor-media-placeholder__url-input-container">
					<Button
						__next40pxDefaultSize
						className="block-editor-media-placeholder__button"
						onClick={ onToggleFeaturedImage }
						variant="secondary"
					>
						{ __( 'Use featured image' ) }
					</Button>
				</div>
			)
		);
	};

	const renderMediaUploadChecked = () => {
		const defaultButton = ( { open }: { open: () => void } ) => {
			return (
				<Button
					__next40pxDefaultSize
					variant="secondary"
					onClick={ () => {
						open();
					} }
				>
					{ __( 'Media Library' ) }
				</Button>
			);
		};
		const libraryButton = mediaLibraryButton ?? defaultButton;
		const uploadMediaLibraryButton = (
			<MediaUpload
				addToGallery={ addToGallery }
				// `add` allows multiple files like `true`.
				gallery={ ( multiple && onlyAllowsImages() ) as boolean }
				multiple={ multiple }
				onSelect={ onSelect }
				allowedTypes={ allowedTypes }
				mode="browse"
				value={
					Array.isArray( value )
						? // Media in a gallery have IDs.
							( value.map( ( { id } ) => id ) as number[] )
						: value.id
				}
				render={ libraryButton }
			/>
		);

		if ( mediaUpload && isAppender ) {
			return (
				<>
					{ renderDropZone() }
					<FormFileUpload
						onChange={ onUpload }
						accept={ computedAccept }
						multiple={ !! multiple }
						render={ ( { openFileDialog } ) => {
							const content = (
								<>
									<Button
										__next40pxDefaultSize
										variant="primary"
										className={ clsx(
											'block-editor-media-placeholder__button',
											'block-editor-media-placeholder__upload-button'
										) }
										onClick={ openFileDialog }
									>
										{ _x( 'Upload', 'verb' ) }
									</Button>
									{ uploadMediaLibraryButton }
									{ renderUrlSelectionUI() }
									{ renderFeaturedImageToggle() }
									{ renderCancelLink() }
								</>
							);
							return renderPlaceholder( content );
						} }
					/>
				</>
			);
		}

		if ( mediaUpload ) {
			const content = (
				<>
					{ renderDropZone() }
					<FormFileUpload
						render={ ( { openFileDialog } ) => (
							<Button
								__next40pxDefaultSize
								onClick={ openFileDialog }
								variant="primary"
								className={ clsx(
									'block-editor-media-placeholder__button',
									'block-editor-media-placeholder__upload-button'
								) }
							>
								{ _x( 'Upload', 'verb' ) }
							</Button>
						) }
						onChange={ onUpload }
						accept={ computedAccept }
						multiple={ !! multiple }
					/>
					{ uploadMediaLibraryButton }
					{ renderUrlSelectionUI() }
					{ renderFeaturedImageToggle() }
					{ renderCancelLink() }
				</>
			);
			return renderPlaceholder( content );
		}

		return renderPlaceholder( uploadMediaLibraryButton );
	};

	if ( disableMediaButtons ) {
		return <MediaUploadCheck>{ renderDropZone() }</MediaUploadCheck>;
	}

	return (
		<MediaUploadCheck
			fallback={ renderPlaceholder( renderUrlSelectionUI() ) }
		>
			{ renderMediaUploadChecked() }
		</MediaUploadCheck>
	);
}

/**
 * @see https://github.com/WordPress/gutenberg/blob/HEAD/packages/block-editor/src/components/media-placeholder/README.md
 */
export default withFilters( 'editor.MediaPlaceholder' )(
	MediaPlaceholder
) as unknown as <
	Media = unknown,
	Value extends MediaPlaceholderMedia = MediaPlaceholderMedia,
>(
	props: MediaPlaceholderProps< Media, Value >
) => ReactNode; // `withFilters` erases the props of the filtered component.
